import React, { useMemo } from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, IconButton, Grid, TextField, Stack, Menu, MenuItem, LinearProgress
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { FaPlus } from "react-icons/fa6";
import CustomButton from "components/Button";
import { useLazyGetMilestoneListQuery } from "store/slices/apis/milestoneApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddMilestone from "./AddMilestone";
import EditMilestone from "./EditMilestone";
import ViewMilestone from "./ViewMilestone";
import { MdDeleteOutline } from "react-icons/md";
import DeleteMilestone from "./DeleteMilestone";
import { usePermission } from "hooks/usePermission";
import { MILESTONE_ACCESS, MILESTONE_CREATE, MILESTONE_DELETE, MILESTONE_VIEW } from "helpers/permissionConfig";
import { enterpriseTokens } from "styles/theme";

const normalize = (value) =>
    (value || "")
        .toString()
        .trim()
        .toUpperCase();

const MilestoneData = () => {

    const { projectId } = useParams();

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditMilestone(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuDelete = () => {
        if (menuRow) {
            handleDeleteClick(menuRow);
        }
        handleMenuClose();
    };

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('createdDate');

    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [isAddMilestone, setIsAddMilestone] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [isDelete, setIsDelete] = useState(false);
    const [deleteData, setDeleteData] = useState(null);

    const createAccess = usePermission(MILESTONE_CREATE);
    const editAccess = usePermission(MILESTONE_ACCESS);
    const deleteAccess = usePermission(MILESTONE_DELETE);
    const viewAccess = usePermission(MILESTONE_VIEW);

    const [filters, setFilters] = useState({
        ProjectId: projectId,
    });

    const [trigger, { data: milestoneList, isLoading, isFetching }] = useLazyGetMilestoneListQuery();
    const milestoneData = milestoneList?.data || [];

    useEffect(() => {
        if (!projectId) return;

        trigger({
            pid: projectId,
            params: filters,
        });
    }, [projectId, filters]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleAddMilestone = () => {
        setIsAddMilestone((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditMilestone = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditMilestoneClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const handleDeleteClick = (row) => {
        setIsDelete((prev) => !prev);
        setDeleteData(row);
    };

    const handleDeleteClose = () => {
        setIsDelete(false);
        setDeleteData(null);
    };

    const processedData = useMemo(() => {

        let result = [...milestoneData];

        if (searchTerm.trim() !== "") {
            const s = searchTerm.toLowerCase();

            result = result.filter((item) =>
                item.milestone?.toLowerCase().includes(s) ||
                item.percentageComplete?.toString().includes(s)
            );
        }

        return result;

    }, [milestoneData, searchTerm]);

    const paginatedData = useMemo(() => {
        const startIndex = (pageNumber - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return processedData.slice(startIndex, endIndex);
    }, [processedData, pageNumber, pageSize]);

    const totalCount = processedData.length;

    const milestoneSummary = useMemo(() => {
        const completed = processedData.filter((item) => normalize(item.status) === "COMPLETED").length;
        const inProgress = processedData.filter((item) => normalize(item.status) === "IN PROGRESS").length;
        const overdue = processedData.filter((item) => item?.dueDate && normalize(item.status) !== "COMPLETED" && moment.utc(item.dueDate).isBefore(moment(), "day")).length;
        return [
            { label: "Total milestones", value: processedData.length, color: "#2563EB" },
            { label: "Completed", value: completed, color: "#16A36A" },
            { label: "In progress", value: inProgress, color: "#E59A16" },
            { label: "Overdue", value: overdue, color: "#E5484D" },
        ];
    }, [processedData]);

    const handleRequestSort = () => () => { };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess || deleteAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>}
                </>
            ),
        },
        {
            name: "Milestone Name",
            field: "milestone",
            width: "200px",
            sticky: true,
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 4, height: 28, bgcolor: normalize(row.status) === "COMPLETED" ? "#16A36A" : normalize(row.status) === "IN PROGRESS" ? "#E59A16" : "#94A3B8" }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: enterpriseTokens.navy }}>{highlightText(row.milestone, searchTerm)}</Typography>
                </Box>
            ),
        },
        {
            name: "Milestone Value (INR Cr.)",
            field: "milestoneValue",
            width: "200px",
            sticky: true,
            renderer: (row) => <Typography sx={{ fontSize: 13, fontWeight: 600, color: enterpriseTokens.navy }}>INR {Number(row.milestoneValue || 0).toFixed(2)} Cr</Typography>,
        },
        {
            name: "Completion (Progress %)",
            field: "percentageComplete",
            sticky: true,
            renderer: (row) => {
                const progress = Math.max(0, Math.min(100, Number(row.percentageComplete || 0)));
                return <Box sx={{ width: 150, display: "flex", alignItems: "center", gap: 1 }}><LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 5, borderRadius: "3px", bgcolor: "#E2E8F0", "& .MuiLinearProgress-bar": { bgcolor: progress >= 100 ? "#16A36A" : "#2563EB" } }} /><Typography sx={{ minWidth: 34, fontSize: 12, fontWeight: 600, color: enterpriseTokens.slate }}>{progress}%</Typography></Box>;
            },
        },
        {
            name: "Due Date",
            field: "dueDate",
            renderer: (row) => {
                const overdue = row?.dueDate && normalize(row.status) !== "COMPLETED" && moment.utc(row.dueDate).isBefore(moment(), "day");
                return <Typography sx={{ fontSize: 12, fontWeight: overdue ? 650 : 500, color: overdue ? "#E5484D" : enterpriseTokens.slate }}>{row?.dueDate ? moment.utc(row.dueDate).format("DD MMM YYYY") : "N/A"}</Typography>;
            },
        },
        {
            name: "Cure Period Due Date",
            field: "curePeriodDueDate",
            renderer: (row) => (
                row?.curePeriodDueDate ? moment.utc(row.curePeriodDueDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: 'Status',
            field: 'status',
            renderer: (row) => (
                <StatusBadge status={row?.status} />
            )
        },
    ];

    return (
        <>
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: `1px solid ${enterpriseTokens.lineSoft}`,
                            pb: 1.5,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: 20, lineHeight: "27px", fontWeight: 650, color: enterpriseTokens.navy }}>Milestones</Typography>
                            <Typography sx={{ mt: 0.2, fontSize: 12, lineHeight: "17px", color: enterpriseTokens.muted }}>Track delivery milestones, completion, and due-date exposure.</Typography>
                        </Box>
                        {createAccess &&
                            <CustomButton
                                label="Create Milestone"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddMilestone}
                                sx={{
                                    backgroundColor: enterpriseTokens.primary,
                                    fontFamily: 'Outfit',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, minmax(0, 1fr))" }, border: `1px solid ${enterpriseTokens.lineSoft}`, borderRadius: "3px", bgcolor: "#FFFFFF" }}>
                        {milestoneSummary.map((item, index) => (
                            <Box key={item.label} sx={{ px: 1.5, py: 1.25, borderRight: index < milestoneSummary.length - 1 ? `1px solid ${enterpriseTokens.lineSoft}` : 0, borderBottom: { xs: index < 2 ? `1px solid ${enterpriseTokens.lineSoft}` : 0, md: 0 } }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}><Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: item.color }} /><Typography sx={{ fontSize: 10, fontWeight: 650, textTransform: "uppercase", color: enterpriseTokens.muted }}>{item.label}</Typography></Box>
                                <Typography sx={{ mt: 0.25, fontSize: 18, lineHeight: "23px", fontWeight: 650, color: enterpriseTokens.navy }}>{item.value}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >
                            </Stack>
                        </Grid>

                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Milestone Name"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', fontSize: 13 },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton aria-label="Refresh milestones" onClick={() => trigger(filters)} sx={{ ml: 0.75, border: `1px solid ${enterpriseTokens.lineSoft}`, borderRadius: "3px" }}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <TableComponent
                        data={paginatedData}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess &&
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>
                        }
                        {editAccess &&
                            <MenuItem
                                onClick={handleMenuEdit}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <EditIcon style={{ width: 14, height: 14 }} />
                                <span>Edit</span>
                            </MenuItem>
                        }
                        {deleteAccess &&
                            <MenuItem
                                onClick={handleMenuDelete}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <MdDeleteOutline style={{ width: 14, height: 14 }} />
                                <span>Delete</span>
                            </MenuItem>
                        }

                    </Menu>

                    <CustomPagination
                        totalCount={totalCount}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >

            {isAddMilestone && <AddMilestone open={isAddMilestone} handleClose={handleAddMilestone} />}
            {isEdit && <EditMilestone open={isEdit} handleClose={handleEditMilestoneClose} rowData={selectedData} />}
            {isView && <ViewMilestone open={isView} handleClose={handleViewClose} rowData={viewData} />}
            {isDelete && <DeleteMilestone open={isDelete} handleClose={handleDeleteClose} rowData={deleteData} />}
        </>
    );
}

export default MilestoneData;
